import { ButtonHTMLAttributes, KeyboardEventHandler, MouseEventHandler, useState } from "react";

import "./buttonNew.css";
import { cn } from "../utils/styling";
import { Icon, IconName } from "./Icon";
import { AdornmentLoading } from "./Loading";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  disabled?: boolean;
  endIconName?: IconName;
  height?: number | string;
  isActive?: boolean;
  isLoading?: boolean;
  size?: "lg" | "md" | "sm" | "xl";
  startIconName?: IconName;
  variant?: "error" | "primary" | "secondary" | "success" | "tertiary";
  width?: number | string;
}

const sizeFontClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
  lg: "font-ds-button-lg",
  md: "font-ds-button-md",
  sm: "font-ds-button-sm",
  xl: "font-ds-button-xl",
};

function tokenVariantColors(
  variant: NonNullable<ButtonProps["variant"]>,
  isPressed: boolean,
  isActive?: boolean
): string {
  switch (variant) {
    case "primary":
      return cn(
        "rounded-ds-0-5x outline outline-transparent",
        "bg-lds-element-primary text-lds-element-primary-on",
        "enabled:hover:bg-lds-element-primary-hover",
        isPressed && "bg-lds-element-primary-pressed enabled:hover:bg-lds-element-primary-pressed",
        "disabled:bg-lds-element-disabled disabled:text-lds-element-disabled-on"
      );
    case "secondary":
      return cn(
        "rounded-ds-0-5x  border-ds-subtle ",
        "bg-lds-element-secondary text-lds-element-secondary-on",
        "enabled:hover:bg-lds-element-secondary-hover enabled:hover:outline-ds-accent",
        (isPressed || isActive) && "!bg-lds-element-secondary-pressed enabled:hover:!bg-lds-element-secondary-pressed",
        "disabled:bg-transparent disabled:text-lds-element-disabled-on-muted disabled:outline-ds-subtle"
      );
    case "tertiary":
      return cn(
        "rounded-ds-0-5x bg-transparent outline-transparent ",
        "enabled:hover:outline-ds-focus enabled:focus:outline-ds-focus",
        isPressed && "!bg-lds-element-secondary-pressed !text-lds-element-secondary-on outline outline-ds-subtle",
        isActive && !isPressed && "!text-lds-link-primary !outline-ds-accent",
        "disabled:bg-transparent disabled:text-lds-element-disabled-on-muted disabled:outline-transparent"
      );
    default:
      return "";
  }
}

export const Button = ({
  children,
  className,
  disabled,
  endIconName,
  height,
  isActive,
  isLoading,
  size = "sm",
  startIconName,
  style,
  variant = "primary",
  width,
  ...props
}: ButtonProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const preventFocusAndTriggerActive: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    setIsPressed(true);
  };
  const untriggerActive: MouseEventHandler<HTMLButtonElement> = () => {
    setIsPressed(false);
  };
  const keyboardTriggerActive: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.code === "Space" || event.code === "Enter") setIsPressed(true);
  };
  const keybordUntriggerActive: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.code === "Space" || event.code === "Enter") setIsPressed(false);
  };

  const usesLegacyCssVariant = variant === "error" || variant === "success";

  return (
    <button
      className={cn(
        "btn flex items-center justify-center",
        `btn-new--${size}`,
        sizeFontClasses[size],
        usesLegacyCssVariant && `btn--${variant}`,
        !usesLegacyCssVariant && tokenVariantColors(variant, isPressed, isActive),
        isPressed && "pressed",
        isActive && "isActive",
        className
      )}
      disabled={disabled ?? isLoading}
      onKeyDown={keyboardTriggerActive}
      onKeyUp={keybordUntriggerActive}
      onMouseDown={preventFocusAndTriggerActive}
      onMouseUp={untriggerActive}
      style={{ height, width, ...style }}
      {...props}
    >
      {startIconName && <Icon className="mr-1" color="currentColor" iconName={startIconName} size={size} />}
      {children}
      {endIconName && <Icon className="ml-1" color="currentColor" iconName={endIconName} size={size} />}
      {isLoading && <AdornmentLoading className="ml-3" sx={{ color: "currentColor" }} />}
    </button>
  );
};
